import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type {
  BidReviewResult,
  CandidateStaffingIssue,
  ContractTermAnalysis,
  CriterionScoreAnalysis,
  DeliverableAnalysis,
  FrameworkAnalysis,
  RequirementAnalysis,
  ReviewPriorityItem,
  RoleAnalysis,
  StaffingAnalysis,
  StaffingFinding,
  StaffingRoleAssessment,
  TimelineCheck,
} from "../types"

import {
  abortBidReview,
  type BidReviewApi,
  deleteTenderDocument,
  getBidReview,
  retryBidReviewPasses,
  startBidReview,
  type TenderCaseApi,
  uploadBidDocument,
} from "@/api/tender-api"

import { TENDER_QUERY_KEYS } from "./use-tender-cases"

// ── Snake-case → camelCase converters for staffing analysis ──────────────────
// The backend stores Pass 6 output via Pydantic's model_dump() which produces
// snake_case keys. The frontend types use camelCase, so we convert explicitly.
// We read each field as `unknown` before narrowing to avoid
// @typescript-eslint/no-unnecessary-condition on the ?? fallbacks.

function asStringArray(val: unknown): string[] {
  return Array.isArray(val) ? (val as string[]) : []
}

function asObjectArray(val: unknown): Record<string, unknown>[] {
  return Array.isArray(val) ? (val as Record<string, unknown>[]) : []
}

function asLiteral<T extends string>(val: unknown, fallback: T): T {
  return typeof val === "string" ? (val as T) : fallback
}

function mapStaffingFinding(raw: Record<string, unknown>): StaffingFinding {
  return {
    findingType: raw["finding_type"] as StaffingFinding["findingType"],
    severity: raw["severity"] as StaffingFinding["severity"],
    description: String(raw["description"] ?? ""),
    affectedCandidates: asStringArray(raw["affected_candidates"]),
  }
}

function mapCandidateIssue(raw: Record<string, unknown>): CandidateStaffingIssue {
  return {
    candidateName: String(raw["candidate_name"] ?? ""),
    missingHardRequirements: asStringArray(raw["missing_hard_requirements"]),
    missingSoftRequirements: asStringArray(raw["missing_soft_requirements"]),
    overqualifiedRequirements: asStringArray(raw["overqualified_requirements"]),
    riskLevel: asLiteral<CandidateStaffingIssue["riskLevel"]>(raw["risk_level"], "medium"),
    note: String(raw["note"] ?? ""),
  }
}

function mapStaffingRoleAssessment(raw: Record<string, unknown>): StaffingRoleAssessment {
  return {
    roleId: String(raw["role_id"] ?? ""),
    roleName: String(raw["role_name"] ?? ""),
    quantityRequired: Number(raw["quantity_required"] ?? 0),
    quantityAssigned: Number(raw["quantity_assigned"] ?? 0),
    coverageStatus: asLiteral<StaffingRoleAssessment["coverageStatus"]>(
      raw["coverage_status"],
      "partial"
    ),
    candidateIssues: asObjectArray(raw["candidate_issues"]).map(mapCandidateIssue),
    roleFindings: asObjectArray(raw["role_findings"]).map(mapStaffingFinding),
    overallAssessment: String(raw["overall_assessment"] ?? ""),
  }
}

function mapStaffingAnalysis(raw: unknown): StaffingAnalysis | undefined {
  if (!raw || typeof raw !== "object") return undefined
  const r = raw as Record<string, unknown>
  return {
    roleAssessments: asObjectArray(r["role_assessments"]).map(mapStaffingRoleAssessment),
    overallStaffingSummary: String(r["overall_staffing_summary"] ?? ""),
    staffingRiskLevel: asLiteral<StaffingAnalysis["staffingRiskLevel"]>(
      r["staffing_risk_level"],
      "medium"
    ),
    unresolvedRoleIds: asStringArray(r["unresolved_role_ids"]),
    recommendedActions: asStringArray(r["recommended_actions"]),
  }
}

// ─────────────────────────────────────────────────────────────────────────────

const BID_REVIEW_QUERY_KEYS = {
  bidReview: (caseId: string, runId: string) => ["tenderBidReview", caseId, runId] as const,
}

function isBidReviewComplete(data: BidReviewApi): boolean {
  return data.status === "completed" || data.status === "failed"
}

function apiBidReviewToResult(api: BidReviewApi): BidReviewResult {
  return {
    id: api.id,
    status: api.status,
    bidDocumentFilenames: api.bidDocumentFilenames,
    overallAssessment: api.overallAssessment ?? undefined,
    complianceScore: api.complianceScore ?? undefined,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- API may return null
    requirementStatuses: (api.requirementStatuses ?? []).map((r) => ({
      requirementId: r.requirementId,
      requirementDescription: r.requirementDescription,
      status: r.status,
      note: r.note ?? undefined,
    })),
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- API may return null
    findings: (api.findings ?? []).map((f) => ({
      ...f,
      relatedRequirementId: f.relatedRequirementId ?? undefined,
      relatedDeliverableId: f.relatedDeliverableId ?? undefined,
      suggestedAction: f.suggestedAction ?? undefined,
    })),
    startedAt: api.startedAt ?? undefined,
    completedAt: api.completedAt ?? undefined,
    error: api.error ?? undefined,
    failureReason: api.failureReason ?? undefined,
    cancelRequested: api.cancelRequested ?? undefined,
    rateLimitRetry: api.rateLimitRetry ?? undefined,
    completedPasses: api.completedPasses ?? undefined,
    passErrors: api.passErrors ?? undefined,
    selectedFrameworks: api.selectedFrameworks ?? undefined,
    requirementAnalyses:
      (api.requirementAnalyses as RequirementAnalysis[] | undefined) ?? undefined,
    roleAnalyses: (api.roleAnalyses as RoleAnalysis[] | undefined) ?? undefined,
    deliverableAnalyses:
      (api.deliverableAnalyses as DeliverableAnalysis[] | undefined) ?? undefined,
    criterionScores: (api.criterionScores as CriterionScoreAnalysis[] | undefined) ?? undefined,
    totalPredictedScore: api.totalPredictedScore ?? undefined,
    totalMaxScore: api.totalMaxScore ?? undefined,
    scoringNotes: api.scoringNotes ?? undefined,
    frameworkAnalyses: (api.frameworkAnalyses as FrameworkAnalysis[] | undefined) ?? undefined,
    contractAnalyses: (api.contractAnalyses as ContractTermAnalysis[] | undefined) ?? undefined,
    timelineChecks: (api.timelineChecks as TimelineCheck[] | undefined) ?? undefined,
    priorityItems: (api.priorityItems as ReviewPriorityItem[] | undefined) ?? undefined,
    improvementOpportunities:
      (api.improvementOpportunities as ReviewPriorityItem[] | undefined) ?? undefined,
    strengthsSummary: api.strengthsSummary ?? undefined,
    competitiveNotes: api.competitiveNotes ?? undefined,
    goNoGo: api.goNoGo ?? undefined,
    goNoGoReasoning: api.goNoGoReasoning ?? undefined,
    staffingPlanIncluded: api.staffingPlanIncluded ?? undefined,
    staffingAnalysis: mapStaffingAnalysis(api.staffingAnalysis),
  }
}

export function useBidReview(
  caseId: string | undefined,
  runId: string | undefined,
  options?: { poll?: boolean }
) {
  const poll = options?.poll ?? true
  return useQuery({
    queryKey: BID_REVIEW_QUERY_KEYS.bidReview(caseId ?? "", runId ?? ""),
    queryFn: () => getBidReview(caseId!, runId!),
    enabled: !!caseId && !!runId,
    select: apiBidReviewToResult,
    retry: 2,
    refetchInterval: (query) => {
      if (!poll) return false
      const data = query.state.data
      if (data && isBidReviewComplete(data)) return false
      if (query.state.status === "error") return data ? 10_000 : false
      if (!data) return 5_000
      return 3_000
    },
  })
}

export function useStartBidReview(caseId: string | undefined) {
  const queryClient = useQueryClient()
  const caseQueryKey = TENDER_QUERY_KEYS.case(caseId ?? "")

  return useMutation({
    mutationFn: ({
      runId,
      frameworks,
      modelId,
    }: {
      runId: string
      frameworks?: string[]
      modelId?: string
    }) => startBidReview(caseId!, runId, { frameworks, modelId }),
    onMutate: async ({ runId }) => {
      const bidReviewQueryKey = BID_REVIEW_QUERY_KEYS.bidReview(caseId ?? "", runId)

      await queryClient.cancelQueries({ queryKey: caseQueryKey })
      await queryClient.cancelQueries({ queryKey: bidReviewQueryKey })

      const previousCase = queryClient.getQueryData<TenderCaseApi>(caseQueryKey)
      const previousBidReview = queryClient.getQueryData<BidReviewApi>(bidReviewQueryKey)

      queryClient.setQueryData<TenderCaseApi>(caseQueryKey, (old) => {
        if (!old) return old
        return {
          ...old,
          runs: old.runs.map((run) => {
            if (run.id !== runId) return run
            return {
              ...run,
              bidReviewStatus: "processing",
            }
          }),
        }
      })

      queryClient.setQueryData<BidReviewApi>(bidReviewQueryKey, (old) => {
        if (!old) return old
        return {
          ...old,
          status: "processing",
          error: undefined,
          failureReason: undefined,
          cancelRequested: false,
          completedAt: undefined,
          completedPasses: [],
          passErrors: {},
        }
      })

      return { previousBidReview, previousCase, runId }
    },
    onError: (_error, _variables, context) => {
      if (!context) return

      queryClient.setQueryData<TenderCaseApi | undefined>(caseQueryKey, context.previousCase)
      if (context.previousBidReview !== undefined) {
        queryClient.setQueryData<BidReviewApi | undefined>(
          BID_REVIEW_QUERY_KEYS.bidReview(caseId ?? "", context.runId),
          context.previousBidReview
        )
      }
    },
    // Settled, not success: the request may have auto-joined the caller
    // server-side before failing, so case state must refresh either way
    // (the refetch overrides the onError rollback with server truth).
    onSettled: (_data, _error, { runId }) => {
      void queryClient.invalidateQueries({
        queryKey: BID_REVIEW_QUERY_KEYS.bidReview(caseId ?? "", runId),
      })
      void queryClient.invalidateQueries({
        queryKey: caseQueryKey,
      })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}

export function useUploadBidDocument(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ runId, files }: { runId: string; files: File[] }): Promise<TenderCaseApi> =>
      uploadBidDocument(caseId!, { runId, files }),
    onSuccess: (data) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.case(caseId ?? ""), data)
    },
    onError: () => {
      // The request may have auto-joined the caller before failing.
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId ?? "") })
    },
  })
}

export function useRemoveBidDocument(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ docId, runId }: { docId: string; runId: string }): Promise<TenderCaseApi> =>
      deleteTenderDocument(caseId!, docId, runId),
    onSuccess: (data) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.case(caseId ?? ""), data)
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId ?? "") })
    },
  })
}

export function useRetryBidReviewPasses(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ runId, passes }: { runId: string; passes: string[] }) =>
      retryBidReviewPasses(caseId!, runId, passes),
    onSettled: (_data, _error, { runId }) => {
      void queryClient.invalidateQueries({
        queryKey: BID_REVIEW_QUERY_KEYS.bidReview(caseId ?? "", runId),
      })
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.case(caseId ?? ""),
      })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}

export function useAbortBidReview(caseId: string | undefined) {
  const queryClient = useQueryClient()
  const caseQueryKey = TENDER_QUERY_KEYS.case(caseId ?? "")

  return useMutation({
    mutationFn: ({ runId }: { runId: string }) => abortBidReview(caseId!, runId),
    onSettled: (_data, _error, { runId }) => {
      void queryClient.invalidateQueries({
        queryKey: BID_REVIEW_QUERY_KEYS.bidReview(caseId ?? "", runId),
      })
      void queryClient.invalidateQueries({
        queryKey: caseQueryKey,
      })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}
